import { useEffect, useState } from "react";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { Button } from "@/components/ui/buttons/button";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { CaretIcon } from "@/icons";
import { useAuth } from "@/providers/auth-provider";
import { getPlayerProfile } from "@/services/player/get-player-profile";
import { sendPokecoins } from "@/services/send/send-pokecoins";
import { Alert } from "@/components/alert/alert";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { useSPAContext } from "@/providers/spa-provider";
import { PokeMiniItem } from "../inventory-poke/poke-mini-item";
import { sendItems } from "@/services/send/send-items";
import { ItemMiniItem } from "../inventory-items/item-mini-item";

export const SendItemsView = () => {
  const { getAccessToken } = useAuth();
  const { selectedItems, setCurrentRoute, setSelectedItems } = useSPAContext();

  const [receiverName, setReceiverName] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSend = async () => {
    // Validation with user feedback
    if (!receiverName.trim()) {
      console.log(`receivername: ${receiverName}`);
      Alert("Please enter a receiver username");
      return;
    }

    if (isLoading) return;

    const accessToken = getAccessToken();
    if (!accessToken) {
      Alert("Authentication error. Please try again");
      return;
    }

    setIsLoading(true);
    try {
      const response = await sendItems(accessToken, {
        items: selectedItems.map((item) => ({
          item_name: item.item_name,
          amount: item.amount,
        })),
        receiver_name: receiverName.trim(),
      });

      if (response.ok && response.data) {
        // Update balance

        // Reset form
        setReceiverName("");

        Alert(
          `Successfully sent ${response.data.items.length} items to ${receiverName}!`,
        );
      } else {
        Alert(response.message || "Failed to send items");
      }
    } catch (error) {
      console.error("❌ Send error:", error);
      Alert("An error occurred while sending items");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedItems.length === 0) {
      setCurrentRoute(undefined);
    }
  }, []);

  const handleCancel = () => {
    setCurrentRoute(undefined);
    setSelectedItems([]);
  };

  const handleDecreaseAmount = (itemName: string) => {
    setSelectedItems((prev) =>
      prev.map((item) =>
        item.item_name === itemName
          ? { ...item, amount: item.amount - 1 }
          : item,
      ),
    );
  };

  const handleIncreaseAmount = (itemName: string) => {
    setSelectedItems((prev) =>
      prev.map((item) =>
        item.item_name === itemName
          ? { ...item, amount: item.amount + 1 }
          : item,
      ),
    );
  };

  const canSend = receiverName.trim().length > 0 && !isLoading;

  return (
    <section className="w-full flex flex-col gap-4 bg-black/80 min-h-screen z-10 pb-24 pt-12">
      {/* Send Form */}
      <div className="flex flex-col mx-4">
        <div className="border-4 border-pk-gray-1">
          <div className="p-4 bg-pk-garnet-3/80 border-4 border-pk-garnet-4 flex flex-col gap-4">
            {/* Receiver Input */}
            <div className="flex flex-col gap-2">
              <label className="text-pk-gray-4 text-sm font-semibold">
                RECEIVER USERNAME
              </label>
              <input
                type="text"
                value={receiverName}
                onChange={(e) => setReceiverName(e.target.value)}
                placeholder="Enter username..."
                className="w-full px-4 py-2 bg-pk-garnet-1 border-2 border-pk-garnet-2 rounded-md text-pk-gray-5 placeholder:text-pk-gray-3 outline-none focus:border-pk-garnet-4 transition"
              />
            </div>

            {/* Items List */}
            <div className="flex flex-col gap-2">
              <label className="text-pk-gray-4 text-sm font-semibold">
                ITEMS TO SEND
              </label>
              <div className="grid grid-cols-4 gap-1 overflow-x-auto">
                {selectedItems.map((item) => (
                  <div className="flex flex-col">
                    <ItemMiniItem
                      key={item.id}
                      itemName={item.item_name}
                      amount={item.amount}
                      selected={true}
                      onClick={() => {}}
                    />
                    {/* Amount Selector */}
                    <div className="flex flex-row gap-1 w-full mt-1 justify-center">
                      <SimpleButton
                        variant="garnet"
                        onClick={() => handleDecreaseAmount(item.item_name)}
                      >
                        -
                      </SimpleButton>
                      <span className="text-pk-gray-5 w-[25px] text-center">
                        {item.amount}
                      </span>
                      <SimpleButton
                        variant="garnet"
                        onClick={() => handleIncreaseAmount(item.item_name)}
                      >
                        +
                      </SimpleButton>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Send Button */}
            <Button
              variant="garnet"
              onClick={handleSend}
              disabled={!canSend}
              className="w-full mt-4 z-10"
            >
              <div className="flex items-center gap-4 justify-center">
                <img
                  src="/assets/items/small/poke-ball.png"
                  alt="Shard"
                  className="w-auto h-6"
                />
                <span className="text-lg">
                  {isLoading ? "SENDING..." : `SEND`}
                </span>
              </div>
            </Button>

            <div
              className="mx-auto transition hover:text-pk-gray-4  text-pk-garnet-4 underline font-medium cursor-pointer mb-2"
              onClick={handleCancel}
            >
              Cancel
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
